/**
 * Browser fullscreen helpers (with vendor prefixes).
 */

function getFullscreenElement(): Element | null {
  const doc = document as Document & {
    webkitFullscreenElement?: Element
    mozFullScreenElement?: Element
    msFullscreenElement?: Element
  }
  return (
    document.fullscreenElement ||
    doc.webkitFullscreenElement ||
    doc.mozFullScreenElement ||
    doc.msFullscreenElement ||
    null
  )
}

export function isFullscreenActive(): boolean {
  return getFullscreenElement() !== null
}

export function isFullscreenSupported(): boolean {
  const doc = document as Document & {
    webkitFullscreenEnabled?: boolean
    mozFullScreenEnabled?: boolean
    msFullscreenEnabled?: boolean
  }
  return !!(
    document.fullscreenEnabled ||
    doc.webkitFullscreenEnabled ||
    doc.mozFullScreenEnabled ||
    doc.msFullscreenEnabled
  )
}

export async function enterFullscreen(element: HTMLElement = document.documentElement): Promise<boolean> {
  if (!isFullscreenSupported()) return false
  if (isFullscreenActive()) return true

  const el = element as HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void>
    mozRequestFullScreen?: () => Promise<void>
    msRequestFullscreen?: () => Promise<void>
  }

  try {
    if (el.requestFullscreen) {
      await el.requestFullscreen()
    } else if (el.webkitRequestFullscreen) {
      await el.webkitRequestFullscreen()
    } else if (el.mozRequestFullScreen) {
      await el.mozRequestFullScreen()
    } else if (el.msRequestFullscreen) {
      await el.msRequestFullscreen()
    } else {
      return false
    }
    return isFullscreenActive()
  } catch (error) {
    console.warn('[Fullscreen] request failed:', error)
    return false
  }
}

export async function exitFullscreen(): Promise<void> {
  const doc = document as Document & {
    webkitExitFullscreen?: () => Promise<void>
    mozCancelFullScreen?: () => Promise<void>
    msExitFullscreen?: () => Promise<void>
  }

  try {
    if (document.exitFullscreen) {
      await document.exitFullscreen()
    } else if (doc.webkitExitFullscreen) {
      await doc.webkitExitFullscreen()
    } else if (doc.mozCancelFullScreen) {
      await doc.mozCancelFullScreen()
    } else if (doc.msExitFullscreen) {
      await doc.msExitFullscreen()
    }
  } catch (error) {
    console.warn('[Fullscreen] exit failed:', error)
  }
}
